/**
 * API Endpoints Configuration
 * Centralized endpoint definitions without /api prefix
 * ApiClient will handle prefix addition automatically
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/v1/Authentication/AuthenticateUser',
    LOGOUT: '/v1/Authentication/Logout',
    // New backend endpoints (use /new_api prefix)
    FORGOT_PASSWORD: '/new_api/auth/forgot-password',
    RESET_PASSWORD: '/new_api/auth/reset-password',
    PREFERRED_OTP_CHANNEL: '/new_api/auth/preferred_otp_channel',
    RESEND_OTP: '/new_api/auth/resend-otp-forgot-password',
    // Legacy endpoint (kept for backward compatibility)
    AUTHENTICATE_OTP: '/v1/Authentication/OTPAuthentication',
  },

  // Corporate
  CORPORATE: {
    GET_ALL: '/v1/Corporate/GetCorporates',
    GET_DEPARTMENTS: '/v1/Corporate/GetAllDepartmentByCorporate',
    STAFF_LIST_BY_CORPORATE: '/v1/Corporate/StaffListByCorporate',
    ADD_REGISTRATION: '/v1/Corporate/AddCorporateRegV2',
    CORPORATE_REGISTRATION: '/v1/Corporate/CorporateRegistration',
    ADD_EDIT_PARAMETER: '/v1/Corporate/AddEditCorporateParameter',
    GET_SHUTTLE_STOP: '/v1/Corporate/GetShuttleStop',
    ADD_EDIT_SHUTTLE_STOP: '/v1/Corporate/AddEditShuttleStop',
  },

  // Staff
  STAFF: {
    GET_DETAILS: '/v1/Staff/GetLittleStaffDetails',
    ADD_EDIT_LITTLE_STAFF: '/v1/Staff/AddEditLittleStaffDetails',
    GET_STAFF_LIST: '/v1/Staff/GetStaffList',
    ADD_EDIT_STAFF_V2: '/v1/Staff/AddEditStafV2',
    TRANSFER_STAFF: '/v1/Staff/TransferStaff',
    SUSPEND_STAFF: '/v1/Staff/SuspendStaff',
  },

  // Department - All endpoints from Postman collection
  DEPARTMENT: {
    GET_LIST: '/v1/Department/DepartmentList',
    GET_DETAILS: '/v1/Department/GetDepartmentDetails',
    ADD_EDIT: '/v1/Department/AddEditDepartment',
    GET_ALL_DEPARTMENT_BY_CORPORATE: '/v1/Corporate/GetAllDepartmentByCorporate',
  },

  // Driver - Aligned with Postman collection
  DRIVER: {
    GET_LIST: '/v1/DriverList/GetDriverList', // Primary driver list endpoint from Postman
    GET_PRIVATE_LIST: '/v1/DriverList/GetPrivateDriverList',
    GET_SHUTTLE_LIST: '/v1/ShuttleTrips/GetShuttleDriverList',
    DRIVER_LIST_V2: '/v1/DriverList/DriverListV2', // Extended driver list from Postman
    GET_ALL_WITH_RIDERS: '/v1/ShuttleTrips/GetAllDriverWithRiderList',
    GET_DETAILS: '/v1/DriverList/GetDriverDetails',
    GET_PARTNERS_WITH_VEHICLES: '/v1/DriverList/GetPartnersWithVehicles',
    ASSIGN_VEHICLE: '/v1/DriverList/GetAssignVehicleToDriver',
    ADD_EDIT_PRIVATE: '/v1/DriverList/AddEditPrivateDriver',
    ADD_EDIT_SHUTTLE: '/v1/ShuttleTrips/AddEditShuttleDriver',
    ONLINE_LOCATIONS: '/v1/DriverList/DriveronlineLocations',
    UPDATE_OVERTIME: '/v1/DriverList/UpdatetDriverOvertime',
    EDIT_ASSIGNED: '/v1/DriverList/EditAssignedDriver',
    // Note: GetDriverLocation, GetDriverLocations, GetAllDriverLocations removed - not in Postman
  },


  // Schedule Booking
  SCHEDULE: {
    GET_TRIPS: '/v1/ScheduleBooking/GetScheduleTrip',
    TRIP_DETAILS: '/v1/ScheduleBooking/ScheduleTripsDetails',
    ADD_EDIT_BOOKING: '/v1/ScheduleBooking/addEditBookingRequest',
  },

  // Shuttle Booking
  SHUTTLE_BOOKING: {
    EDIT_ROUTE: '/v1/ShuttleBooking/EditRouteBooking',
    CANCEL_ROUTE: '/v1/ShuttleBooking/CancelledRouteBooking',
  },

  // Shuttle Trips
  SHUTTLE_TRIPS: {
    GET_SHUTTLE_DRIVER_LIST: '/v1/ShuttleTrips/GetShuttleDriverList',
    ADD_EDIT_DRIVER_WITH_RIDER: '/v1/ShuttleTrips/AddEditDriverwithRiderAssing',
  },

  // Adhoc Drivers
  ADHOC_DRIVERS: {
    ADD_EDIT: '/v1/AdhocDrivers/AddEditAdhocDrivers',
  },

  // Header
  HEADER: {
    CHANGE_PASSWORD: '/v1/Header/ChangePassword',
    GET_NOTIFICATION: '/v1/Header/GetNotification',
  },

  // Menu
  MENU: {
    GET_USER_LIST: '/v1/Menu/GetUserMenuList',
  },

  // Dashboard
  DASHBOARD: {
    GET_DASHBOARD: '/v1/Dashboard/GetDashboard',
  },

  // Live Trip
  LIVE_TRIP: {
    GET_DETAILS: '/v1/LiveTrip/GetLiveTripDetails',
  },

  // Trips
  TRIPS: {
    GET_ALL: '/v1/Trips/GetAllTrips',
    GET_DETAILS: '/v1/Trips/GetTripDetails',
    GET_SHUTTLE: '/v1/ShuttleTrips/GetShuttleTrips',
    ALL_TRIPS_DETAILS: '/v1/Trips/AllTripsDetails',
    SHUTTLE_TRIPS_DETAILS: '/v1/ShuttleTrips/ShuttleTripsDetails',
  },

  // Settings
  SETTINGS: {
    GET_SHIFT_TASK: '/v1/ShiftTask/GetShiftTask',
    ADD_EDIT_SHIFT_TASK: '/v1/ShiftTask/AddEditShiftTask',
  },

  // Route Management - Aligned with Postman (uses singular 'Route')
  ROUTES: {
    // All Route endpoints use singular 'Route' per Postman collection
    GET_LIST: '/v1/Route/GetRoutList', // Primary route list from Postman
    ADD_EDIT: '/v1/Route/AddEditRoute',
    ENABLE_DISABLE_SHUTTLE: '/v1/Route/EnableDisableShuttleRoute',
    GET_BOOKED_ROUTE: '/v1/Route/GetBookedRoute',
    // Note: /v1/Routes/GetAllRoutes and /v1/Routes/DeleteRoute removed - not in Postman
  },

  // Singular Route group (matches backend endpoints used by TestAddRoute components)
  ROUTE: {
    GET_DETAILS: '/v1/Route/GetRouteDetails',
    ADD_EDIT: '/v1/Route/AddEditRoute',
    GET_LIST: '/v1/Route/GetRoutList',
  },

  // Authentication
  AUTHENTICATION: {
    AUTHENTICATE_USER: '/v1/Authentication/AuthenticateUser',
    AUTHENTICATE_OTP: '/v1/Authentication/OTPAuthentication', // Keep original for backward compatibility
    SET_NEW_PASSWORD: '/v1/Authentication/SetNewPassword',
  },

  // Schedule Booking Extended - All endpoints from Postman
  SCHEDULE_BOOKING: {
    GET_BOOKING_REQUEST_DETAILS: '/v1/ScheduleBooking/GetBookingRequestDetails',
    CANCEL_SCHEDULE_TRIP: '/v1/ScheduleBooking/CancelScheduleTrip',
    GET_BOOKING_LIST: '/v1/ScheduleBooking/GetScheduleBookingList',
    ADD_EDIT: '/v1/ScheduleBooking/AddEditScheduleBooking',
    GET_BOOKING_REQUEST_DETAILS_REPORT: '/v1/ScheduleBooking/GetBookingRequestDetailsReport',
    SCHEDULE_TRIP_REPORT: '/v1/ScheduleBooking/ScheduleTripReport',
  },

  // Private Trip - All endpoints from Postman
  PRIVATE_TRIP: {
    GET_PRIVATE_TRIP: '/v1/PrivateTrip/GetPrivateTrip',
    GET_DETAILS: '/v1/PrivateTrip/PrivateTripDetails',
  },

  // Driver Shift - All endpoints from Postman
  DRIVER_SHIFT: {
    GET_DRIVER_SHIFT_DETAILS: '/v1/DriverShift/GetDriverShiftDetails',
    ADD_EDIT_DRIVER_SHIFT: '/v1/DriverShift/AddEditDriverShift',
    GET_DRIVER_SHIFT_LIST: '/v1/DriverShift/GetDriverShiftList',
    DRIVER_SHIFT_DETAILS_REPORT: '/v1/DriverShift/DriverShiftDetailsReport',
  },

  // Admin Role
  ADMIN_ROLE: {
    GET_ADMIN_INFORMATION: '/v1/AdminRole/GetAdminInformation',
  },

  // Document
  DOCUMENT: {
    GET_CORPORATE_DOCUMENT: '/v1/Document/GetCorporateDocument',
    ADD_CORPORATE_DOCUMENT: '/v1/Document/AddCorporateDocument',
  },

  // Additional Driver Operations
  DRIVER_EXTENDED: {
    GET_PRIVATE_DRIVER_LIST: '/v1/DriverList/GetPrivateDriverList',
    GET_DRIVER_DETAILS: '/v1/DriverList/GetDriverDetails',
    ADD_EDIT_PRIVATE_DRIVER: '/v1/DriverList/AddEditPrivateDriver',
    ADD_EDIT_SHUTTLE_DRIVER: '/v1/ShuttleTrips/AddEditShuttleDriver',
    UPDATE_DRIVER_OVERTIME: '/v1/DriverList/UpdatetDriverOvertime',
    EDIT_ASSIGNED_DRIVER: '/v1/DriverList/EditAssignedDriver',
    GET_PARTNERS_WITH_VEHICLES: '/v1/DriverList/GetPartnersWithVehicles',
    GET_ASSIGN_VEHICLE_TO_DRIVER: '/v1/DriverList/GetAssignVehicleToDriver',
  },

  // Shuttle Booking Extended
  SHUTTLE_BOOKING_EXTENDED: {
    GET_BOOKED_RIDER_DETAILS: '/v1/ShuttleBooking/GetBookedRiderDetails',
  },

  // Corporate Extended
  CORPORATE_EXTENDED: {
    ADD_CORPORATE_REG_V2: '/v1/Corporate/AddCorporateRegV2',
    CORPORATE_REGISTRATION: '/v1/Corporate/CorporateRegistration',
    ADD_EDIT_CORPORATE_PARAMETER: '/v1/Corporate/AddEditCorporateParameter',
    ADD_EDIT_SHUTTLE_STOP: '/v1/Corporate/AddEditShuttleStop',
  },

  // Report endpoints from Postman collection
  REPORT: {
    SHUTTLE_TRIP_REPORT: '/v1/Report/ShuttleTripReport',
  },
};

// Legacy endpoint mapping for gradual migration
export const LEGACY_ENDPOINTS = {
  '/v1/Authentication/AuthenticateUser': API_ENDPOINTS.AUTHENTICATION.AUTHENTICATE_USER,
  '/v1/Authentication/OTPAuthentication': API_ENDPOINTS.AUTHENTICATION.AUTHENTICATE_OTP, // Keep original for backward compatibility
  '/v1/Authentication/SetNewPassword': API_ENDPOINTS.AUTHENTICATION.SET_NEW_PASSWORD,
  '/v1/Corporate/GetCorporates': API_ENDPOINTS.CORPORATE.GET_ALL,
  '/v1/Corporate/GetAllDepartmentByCorporate': API_ENDPOINTS.CORPORATE.GET_DEPARTMENTS,
  '/v1/Corporate/StaffListByCorporate': API_ENDPOINTS.CORPORATE.STAFF_LIST_BY_CORPORATE,
  '/v1/Corporate/GetShuttleStop': API_ENDPOINTS.CORPORATE.GET_SHUTTLE_STOP,
  '/v1/Corporate/AddCorporateRegV2': API_ENDPOINTS.CORPORATE_EXTENDED.ADD_CORPORATE_REG_V2,
  '/v1/Corporate/CorporateRegistration': API_ENDPOINTS.CORPORATE_EXTENDED.CORPORATE_REGISTRATION,
  '/v1/Corporate/AddEditCorporateParameter': API_ENDPOINTS.CORPORATE_EXTENDED.ADD_EDIT_CORPORATE_PARAMETER,
  '/v1/Corporate/AddEditShuttleStop': API_ENDPOINTS.CORPORATE_EXTENDED.ADD_EDIT_SHUTTLE_STOP,
  '/v1/Department/DepartmentList': API_ENDPOINTS.DEPARTMENT.GET_LIST,
  '/v1/Department/GetDepartmentDetails': API_ENDPOINTS.DEPARTMENT.GET_DETAILS,
  '/v1/Department/AddEditDepartment': API_ENDPOINTS.DEPARTMENT.ADD_EDIT,
  '/v1/Route/GetRouteDetails': API_ENDPOINTS.ROUTE.GET_DETAILS,
  '/v1/Route/AddEditRoute': API_ENDPOINTS.ROUTE.ADD_EDIT,
  '/v1/Route/GetRoutList': API_ENDPOINTS.ROUTE.GET_LIST,
  '/v1/Route/EnableDisableShuttleRoute': API_ENDPOINTS.ROUTES.ENABLE_DISABLE_SHUTTLE,
  '/v1/Route/GetBookedRoute': API_ENDPOINTS.ROUTES.GET_BOOKED_ROUTE,
  '/v1/DriverList/GetDriverList': API_ENDPOINTS.DRIVER.GET_LIST, // Uses proper Postman endpoint
  '/v1/DriverList/DriverListV2': API_ENDPOINTS.DRIVER.DRIVER_LIST_V2, // Uses proper Postman endpoint
  '/v1/DriverList/DriveronlineLocations': API_ENDPOINTS.DRIVER.ONLINE_LOCATIONS,
  '/v1/DriverList/GetPrivateDriverList': API_ENDPOINTS.DRIVER_EXTENDED.GET_PRIVATE_DRIVER_LIST,
  '/v1/DriverList/GetDriverDetails': API_ENDPOINTS.DRIVER_EXTENDED.GET_DRIVER_DETAILS,
  '/v1/DriverList/AddEditPrivateDriver': API_ENDPOINTS.DRIVER_EXTENDED.ADD_EDIT_PRIVATE_DRIVER,
  '/v1/DriverList/UpdatetDriverOvertime': API_ENDPOINTS.DRIVER_EXTENDED.UPDATE_DRIVER_OVERTIME,
  '/v1/DriverList/EditAssignedDriver': API_ENDPOINTS.DRIVER_EXTENDED.EDIT_ASSIGNED_DRIVER,
  '/v1/DriverList/GetPartnersWithVehicles': API_ENDPOINTS.DRIVER_EXTENDED.GET_PARTNERS_WITH_VEHICLES,
  '/v1/DriverList/GetAssignVehicleToDriver': API_ENDPOINTS.DRIVER_EXTENDED.GET_ASSIGN_VEHICLE_TO_DRIVER,
  '/v1/Staff/GetLittleStaffDetails': API_ENDPOINTS.STAFF.GET_DETAILS,
  '/v1/Staff/AddEditLittleStaffDetails': API_ENDPOINTS.STAFF.ADD_EDIT_LITTLE_STAFF,
  '/v1/Staff/GetStaffList': API_ENDPOINTS.STAFF.GET_STAFF_LIST,
  '/v1/Staff/AddEditStafV2': API_ENDPOINTS.STAFF.ADD_EDIT_STAFF_V2,
  '/v1/Staff/TransferStaff': API_ENDPOINTS.STAFF.TRANSFER_STAFF,
  '/v1/Staff/SuspendStaff': API_ENDPOINTS.STAFF.SUSPEND_STAFF,
  '/v1/Trips/GetAllTrips': API_ENDPOINTS.TRIPS.GET_ALL,
  '/v1/Trips/GetTripDetails': API_ENDPOINTS.TRIPS.GET_DETAILS,
  '/v1/Trips/AllTripsDetails': API_ENDPOINTS.TRIPS.ALL_TRIPS_DETAILS,
  '/v1/ShuttleTrips/GetShuttleTrips': API_ENDPOINTS.TRIPS.GET_SHUTTLE,
  '/v1/ShuttleTrips/ShuttleTripsDetails': API_ENDPOINTS.TRIPS.SHUTTLE_TRIPS_DETAILS,
  '/v1/ShuttleTrips/GetShuttleDriverList': API_ENDPOINTS.SHUTTLE_TRIPS.GET_SHUTTLE_DRIVER_LIST,
  '/v1/ShuttleTrips/AddEditDriverwithRiderAssing': API_ENDPOINTS.SHUTTLE_TRIPS.ADD_EDIT_DRIVER_WITH_RIDER,
  '/v1/ShuttleTrips/AddEditShuttleDriver': API_ENDPOINTS.DRIVER.ADD_EDIT_SHUTTLE,
  '/v1/ShuttleBooking/GetBookedRiderDetails': API_ENDPOINTS.SHUTTLE_BOOKING_EXTENDED.GET_BOOKED_RIDER_DETAILS,
  '/v1/ShuttleBooking/EditRouteBooking': API_ENDPOINTS.SHUTTLE_BOOKING.EDIT_ROUTE,
  '/v1/ShuttleBooking/CancelledRouteBooking': API_ENDPOINTS.SHUTTLE_BOOKING.CANCEL_ROUTE,
  '/v1/AdhocDrivers/AddEditAdhocDrivers': API_ENDPOINTS.ADHOC_DRIVERS.ADD_EDIT,
  '/v1/ScheduleBooking/addEditBookingRequest': API_ENDPOINTS.SCHEDULE.ADD_EDIT_BOOKING,
  '/v1/ScheduleBooking/GetBookingRequestDetails': API_ENDPOINTS.SCHEDULE_BOOKING.GET_BOOKING_REQUEST_DETAILS,
  '/v1/ScheduleBooking/GetScheduleTrip': API_ENDPOINTS.SCHEDULE.GET_TRIPS,
  '/v1/ScheduleBooking/ScheduleTripsDetails': API_ENDPOINTS.SCHEDULE.TRIP_DETAILS,
  '/v1/ScheduleBooking/CancelScheduleTrip': API_ENDPOINTS.SCHEDULE_BOOKING.CANCEL_SCHEDULE_TRIP,
  '/v1/Header/ChangePassword': API_ENDPOINTS.HEADER.CHANGE_PASSWORD,
  '/v1/Menu/GetUserMenuList': API_ENDPOINTS.MENU.GET_USER_LIST,
  '/v1/Dashboard/GetDashboard': API_ENDPOINTS.DASHBOARD.GET_DASHBOARD,
  '/v1/LiveTrip/GetLiveTripDetails': API_ENDPOINTS.LIVE_TRIP.GET_DETAILS,
  '/v1/PrivateTrip/GetPrivateTrip': API_ENDPOINTS.PRIVATE_TRIP.GET_PRIVATE_TRIP,
  '/v1/DriverShift/GetDriverShiftDetails': API_ENDPOINTS.DRIVER_SHIFT.GET_DRIVER_SHIFT_DETAILS,
  '/v1/DriverShift/AddEditDriverShift': API_ENDPOINTS.DRIVER_SHIFT.ADD_EDIT_DRIVER_SHIFT,
  '/v1/ShiftTask/GetShiftTask': API_ENDPOINTS.SETTINGS.GET_SHIFT_TASK,
  '/v1/ShiftTask/AddEditShiftTask': API_ENDPOINTS.SETTINGS.ADD_EDIT_SHIFT_TASK,
  '/v1/AdminRole/GetAdminInformation': API_ENDPOINTS.ADMIN_ROLE.GET_ADMIN_INFORMATION,
  '/v1/Document/GetCorporateDocument': API_ENDPOINTS.DOCUMENT.GET_CORPORATE_DOCUMENT,
  '/v1/Document/AddCorporateDocument': API_ENDPOINTS.DOCUMENT.ADD_CORPORATE_DOCUMENT,
};

export default API_ENDPOINTS;